import { Formik, Form } from "formik";
import Modal from "../Modal";
import Toast from "../../Toast/Toast";
import styles from "./LoginModal.module.css";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import DynamicField from "../../DynamicForm/DynamicField";
import { getLoginSchema } from "../../../utils/validationSchema";
import { useLanguage } from "../../../context/LanguageContext";
import { useMenu } from "../../../context/MenuContext";

type AuthMode = "login" | "register" | "forgot";

interface ToastState {
  message: string;
  type: "success" | "error" | "info" | "code";
}

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchMode: (mode: AuthMode) => void;
  onLogin?: (values: { login: string; password: string }) => Promise<any>;
  on2faVerify?: (values: { login: string; password: string; code: string }) => Promise<void>;
  registrationEnabled?: boolean;
  resetEnabled?: boolean;
  tgBot?: string;
}

export default function LoginModal({
  isOpen,
  onClose,
  onSwitchMode,
  onLogin,
  on2faVerify,
  tgBot = '',
  registrationEnabled = true,
  resetEnabled = true,
}: LoginModalProps) {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { loadMenu } = useMenu();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [requires2fa, setRequires2fa] = useState(false);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "code" = "success"
  ) => {
    setToast({ message, type });
  };

  const getErrorMessage = (error: any): string => {
    const errorKey = error.response?.data?.error;

    if (errorKey) {
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }

    const apiMessage = error.response?.data?.message;
    if (apiMessage) {
      const translated = t(apiMessage);
      if (translated !== apiMessage) {
        return translated;
      }
    }

    const errorMessage = error.message;
    if (errorMessage) {
      const translated = t(errorMessage);
      if (translated !== errorMessage) {
        return translated;
      }
    }

    return t("loginFailed");
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setRequires2fa(false);
          onClose();
        }}
        title={t("loginTitle")}
        closeOnOverlayClick={false}
      >
        <Formik
          initialValues={{ login: "", password: "", code: "" }}
          validationSchema={getLoginSchema(t)}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (!requires2fa) {
                if (onLogin) {
                  const apiValues = {
                    login: String(values.login || "").toLowerCase().trim(),
                    password: String(values.password || "")
                  };

                  const result = await onLogin(apiValues);

                  if (result.requires2fa) {
                    setRequires2fa(true);
                    showToast(t("enter2faCodeMessage"), "code");
                    setSubmitting(false);
                    return;
                  } else {
                    showToast(t("loginSuccess"), "success");

                    setTimeout(() => {
                      setRequires2fa(false);
                      onClose();
                      navigate("/dashboard");
                    }, 1000);
                  }
                }
              } else {
                if (on2faVerify) {
                  const verify2faValues = {
                    login: String(values.login || "").toLowerCase().trim(),
                    password: String(values.password || ""),
                    code: String(values.code || "").trim()
                  };

                  await on2faVerify(verify2faValues);

                  showToast(t("loginSuccess"), "success");

                  setTimeout(() => {
                    setRequires2fa(false);
                    onClose();
                    navigate("/dashboard");
                  }, 1000);
                }
              }
            } catch (error: any) {
              showToast(getErrorMessage(error), "error");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values }) => (
            <Form className={styles.form}>
              <DynamicField
                field={{
                  type: "string",
                  name: "login",
                  label: "loginOrEmail",
                  placeholder: "enterLoginOrEmail",
                  disabled: requires2fa,
                }}
              />

              <p className={styles.helpText}>
                <i className="fa fa-lightbulb-o"></i> {t("telegramTgHint")}{" "}
                <a
                  href={`https://t.me/${tgBot?.substring(1)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.helpLink}
                >
                  <i className="fa fa-telegram"></i> {tgBot}
                </a>
              </p>
              <DynamicField
                field={{
                  type: "password",
                  name: "password",
                  label: "password",
                  placeholder: "enterPassword",
                  disabled: requires2fa,
                }}
              />

              {requires2fa && (
                <DynamicField
                  field={{
                    type: "string",
                    name: "code",
                    label: "2faCode",
                    placeholder: "enter2faCode",
                  }}
                />
              )}

              <button
                type="submit"
                className={styles.submitButton}
                disabled={
                  isSubmitting ||
                  (requires2fa && !values.code.trim())
                }
              >
                {isSubmitting ? t("loading") : (requires2fa ? t("verify") : t("login"))}
              </button>

              {!requires2fa && (
                <div className={styles.links}>
                  {resetEnabled && (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => onSwitchMode("forgot")}
                    >
                      {t("forgotPassword")}
                    </button>
                  )}
                  {registrationEnabled && (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => onSwitchMode("register")}
                    >
                      {t("noAccount")}
                    </button>
                  )}
                </div>
              )}

              {requires2fa && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    setRequires2fa(false);
                  }}
                >
                  {t("back")}
                </button>
              )}
            </Form>
          )}
        </Formik>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}